import type { ReactNode } from "react";

import { PlainValue } from "@/components/plain-value";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { cn } from "@/lib/utils/cn";

export type Column<T> = ColumnLayout &
    (
        | {
              /** `null` is shown as a dash. */
              value: (row: T) => string | number | null;
              render?: never;
          }
        | {
              /** A composite cell, such as a list of tokens. */
              render: (row: T) => ReactNode;
              value?: never;
          }
    );

interface ColumnLayout {
    label: string;
    className?: string;
    /** On a phone card, span the whole row: long values such as hashes and addresses. */
    wide?: boolean;
    /** On a phone card, shown in the card's title line instead of the field grid. */
    title?: boolean;
    /** Shortened for display (one line in a table, three on a card); the full value shows on hover. */
    truncate?: boolean;
}

interface DataListProps<T> {
    rows: T[];
    columns: Column<T>[];
    rowKey: (row: T) => string;
    label: string;
}

/**
 * Every field of every row at any width: a card per row on a phone, where a wide table would hide
 * most columns behind a sideways scroll, and a full table from `md` up. Both are rendered and CSS
 * picks one, so there is no layout flash and no resize listener.
 */
export function DataList<T>({ rows, columns, rowKey, label }: DataListProps<T>) {
    const [titleColumn, ...subtitleColumns] = columns.filter((column) => column.title);
    const fields = columns.filter((column) => !column.title);

    return (
        <>
            <ul className="space-y-2 md:hidden" aria-label={label}>
                {rows.map((row) => (
                    <li key={rowKey(row)} className="rounded-lg border bg-card p-3 shadow-sm">
                        {titleColumn && (
                            <div className="mb-2.5 flex items-baseline justify-between gap-2 border-b pb-2">
                                <span className="min-w-0 text-base font-semibold">
                                    <Value column={titleColumn} row={row} layout="title" />
                                </span>
                                {subtitleColumns.map((column) => (
                                    <span key={column.label} className="shrink-0 text-sm text-muted-foreground">
                                        <Value column={column} row={row} layout="title" />
                                    </span>
                                ))}
                            </div>
                        )}
                        <dl className="grid grid-cols-2 gap-x-3 gap-y-2.5 min-[360px]:grid-cols-3">
                            {fields.map((column) => (
                                <div key={column.label} className={cn("min-w-0", column.wide && "col-span-full")}>
                                    <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                                        {column.label}
                                    </dt>
                                    <dd className={cn("text-[0.9375rem]", column.className)}>
                                        <Value column={column} row={row} layout="card" />
                                    </dd>
                                </div>
                            ))}
                        </dl>
                    </li>
                ))}
            </ul>
            <div className="hidden md:block">
                <Table aria-label={label}>
                    <TableHeader>
                        <TableRow>
                            {columns.map((column) => (
                                <TableHead key={column.label}>{column.label}</TableHead>
                            ))}
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {rows.map((row) => (
                            <TableRow key={rowKey(row)}>
                                {columns.map((column) => (
                                    <TableCell key={column.label} className={column.className}>
                                        <Value column={column} row={row} layout="table" />
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>
        </>
    );
}

// A card wraps a long value onto more lines. A table cell never wraps: a value that can break
// anywhere lets the table squeeze its column to one character.
const TEXT: Record<"title" | "card" | "table", string> = {
    title: "block truncate",
    card: "block break-all",
    table: "block whitespace-nowrap",
};

function Value<T>({ column, row, layout }: { column: Column<T>; row: T; layout: keyof typeof TEXT }) {
    if (column.render) return column.render(row);
    const value = column.value(row);
    if (value === null) return <span className="text-muted-foreground">—</span>;
    const truncated = Boolean(column.truncate) && layout !== "title";
    return (
        <PlainValue
            value={value}
            label={column.label}
            truncated={truncated}
            className={cn(
                TEXT[layout],
                truncated && layout === "table" && "max-w-64 truncate",
                truncated && layout === "card" && "line-clamp-3",
            )}
        />
    );
}
